import { zodResolver } from '@hookform/resolvers/zod'
import { applicationCreateDirect, type DirectRegistration } from '@repo/sdk/gen'
import { Alert, AlertDescription, AlertTitle } from '@repo/ui/alert'
import {
	AlertDialog,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle
} from '@repo/ui/alert-dialog'
import { Button } from '@repo/ui/button'
import { Skeleton } from '@repo/ui/skeleton'
import { Spinner } from '@repo/ui/spinner'
import { cn, formatError } from '@repo/ui/utils'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useBlocker } from '@tanstack/react-router'
import { useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { useTranslation } from '@/lib/i18n'
import { makeQueryKeys } from '@/query/query-keys'
import { romeInputs } from '../../../-lib/rome-time'
import { managedCallQueryOptions } from '../../../-query/query-options'
import { placesAreSet, transcriptionIssueMessage } from '../-lib/transcription'
import { DuplicatesStep } from './-components/duplicates-step'
import { MessageStep } from './-components/message-step'
import { RegisteredPanel } from './-components/registered-panel'
import { SummaryStep } from './-components/summary-step'
import { TranscriptionStep } from './-components/transcription-step'
import {
	EMPTY_INTAKE,
	INTAKE_STEPS,
	type IntakeStep,
	type IntakeValues,
	intakeFormSchema,
	STEP_FIELDS,
	toCreateDirect
} from './-lib/intake-form'

export const Route = createFileRoute(
	'/(private)/bando/$residenceId/domande/carica/'
)({
	staticData: {
		breadcrumb: 'admin-applications:intake.page_title'
	},
	component: IntakePage
})

/**
 * "Carica domanda ricevuta via PEC" (FFD 2.4): the message, the transcription,
 * the applications of the same person, the summary. The steps live in the page
 * and not in the URL: what is typed is not kept, so a step reloaded alone
 * would open on an empty form.
 */
function IntakePage() {
	const { t } = useTranslation()
	const { residenceId } = Route.useParams()
	const call = useQuery(managedCallQueryOptions(residenceId))

	if (call.isLoading) {
		return (
			<div className='p-4 md:p-6'>
				<Skeleton className='h-64 w-full' />
			</div>
		)
	}
	if (call.data?.intake.state !== 'open') {
		return (
			<Alert className='m-4 max-w-2xl md:m-6'>
				<AlertTitle>
					{t('admin-applications:intake.closed_title')}
				</AlertTitle>
				<AlertDescription>
					{t('admin-applications:intake.closed_description')}
				</AlertDescription>
			</Alert>
		)
	}
	return <IntakeForm residenceId={residenceId} />
}

function IntakeForm({ residenceId }: { residenceId: string }) {
	const { t } = useTranslation()
	const queryClient = useQueryClient()
	const [step, setStep] = useState<IntakeStep>('message')
	const [message, setMessage] = useState<File | null>(null)
	const [registered, setRegistered] = useState<DirectRegistration | null>(
		null
	)
	const form = useForm<IntakeValues>({
		resolver: zodResolver(intakeFormSchema, {
			error: transcriptionIssueMessage
		}),
		defaultValues: EMPTY_INTAKE,
		mode: 'onTouched'
	})
	const index = INTAKE_STEPS.indexOf(step)

	const create = useMutation({
		mutationFn: (values: IntakeValues) =>
			applicationCreateDirect({
				body: { data: toCreateDirect(residenceId, values) }
			}),
		onSuccess: async (response) => {
			setRegistered(response.data)
			await Promise.all([
				queryClient.invalidateQueries({
					queryKey: makeQueryKeys.applications.all()
				}),
				queryClient.invalidateQueries({
					queryKey: makeQueryKeys.call.all()
				})
			])
		},
		onError: (error) => {
			const code = 'code' in error ? String(error.code) : ''
			if (
				code === 'direct_arrived_after_close' ||
				code === 'direct_arrived_out_of_window'
			) {
				setStep('message')
				form.setError('arrivalDate', {
					type: 'server',
					message: t(`admin-applications:intake.errors.${code}`)
				})
				return
			}
			if (
				code === 'direct_arrival_not_certified' &&
				'certifiedAt' in error &&
				typeof error.certifiedAt === 'string'
			) {
				// The PEC certifies another arrival: the form takes it, to the second.
				const { date, time } = romeInputs(error.certifiedAt, {
					seconds: true
				})
				setStep('message')
				form.setValue('arrivalDate', date)
				form.setValue('arrivalTime', time)
				form.setError('arrivalTime', {
					type: 'server',
					message: t(
						'admin-applications:intake.errors.direct_arrival_not_certified',
						{ date, time }
					)
				})
				return
			}
			if (code === 'direct_tax_code_invalid') {
				setStep('transcription')
				form.setError('transcription.taxCode', {
					type: 'server',
					message: t(
						'admin-applications:intake.errors.direct_tax_code_invalid'
					)
				})
				return
			}
			toast.error(
				code === 'direct_intake_closed'
					? t('admin-applications:intake.closed_title')
					: formatError(error)
			)
		}
	})

	const blocker = useBlocker({
		shouldBlockFn: () => true,
		disabled: !form.formState.isDirty || registered !== null,
		enableBeforeUnload: form.formState.isDirty && registered === null,
		withResolver: true
	})

	async function next() {
		const fields = [...STEP_FIELDS[step]]
		const ok = fields.length === 0 || (await form.trigger(fields))
		if (!ok) {
			toast.error(t('admin-applications:intake.fix_fields'))
			return
		}
		if (
			step === 'transcription' &&
			!placesAreSet(form.getValues('transcription'))
		) {
			toast.error(t('admin-applications:intake.places_required'))
			return
		}
		const following = INTAKE_STEPS[index + 1]
		if (following) {
			setStep(following)
		}
	}

	function submit() {
		if (!form.getValues('reviewed')) {
			form.setError('reviewed', { message: 'required' })
			return
		}
		create.mutate(form.getValues())
	}

	if (registered) {
		return (
			<div className='mx-auto w-full max-w-5xl p-4 md:p-6'>
				<RegisteredPanel
					registered={registered}
					residenceId={residenceId}
				/>
			</div>
		)
	}

	return (
		<FormProvider {...form}>
			<div className='mx-auto flex w-full min-w-0 max-w-5xl flex-col gap-6 p-4 md:p-6'>
				<header className='flex flex-col gap-1'>
					<h1 className='font-semibold text-2xl'>
						{t('admin-applications:intake.page_title')}
					</h1>
					<p className='text-muted-foreground text-sm'>
						{t('admin-applications:intake.description')}
					</p>
				</header>
				<ol className='flex flex-wrap gap-2'>
					{INTAKE_STEPS.map((name, position) => (
						<li
							aria-current={name === step ? 'step' : undefined}
							className={cn(
								'rounded-full border px-3 py-1 text-sm',
								name === step
									? 'border-primary bg-primary/5 font-medium'
									: 'text-muted-foreground'
							)}
							key={name}
						>
							{position + 1}.{' '}
							{t(`admin-applications:intake.steps.${name}`)}
						</li>
					))}
				</ol>
				{step === 'message' && (
					<MessageStep
						onMessage={setMessage}
						residenceId={residenceId}
					/>
				)}
				{step === 'transcription' && (
					<TranscriptionStep residenceId={residenceId} />
				)}
				{step === 'duplicates' && (
					<DuplicatesStep residenceId={residenceId} />
				)}
				{step === 'summary' && <SummaryStep message={message} />}
				<div className='flex flex-wrap justify-between gap-3 border-t pt-6'>
					<Button
						disabled={index === 0}
						onClick={() => {
							const previous = INTAKE_STEPS[index - 1]
							if (previous) {
								setStep(previous)
							}
						}}
						type='button'
						variant='outline'
					>
						{t('admin-applications:intake.back')}
					</Button>
					{step === 'summary' ? (
						<Button
							disabled={create.isPending}
							onClick={submit}
							type='button'
						>
							{create.isPending && <Spinner />}
							{t('admin-applications:intake.register')}
						</Button>
					) : (
						<Button onClick={next} type='button'>
							{t('admin-applications:intake.next')}
						</Button>
					)}
				</div>
			</div>
			<AlertDialog
				onOpenChange={(open) => {
					if (!open) {
						blocker.reset?.()
					}
				}}
				open={blocker.status === 'blocked'}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							{t('admin-applications:intake.leave.title')}
						</AlertDialogTitle>
						<AlertDialogDescription>
							{t('admin-applications:intake.leave.description')}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>
							{t('admin-applications:intake.leave.stay')}
						</AlertDialogCancel>
						<Button
							onClick={() => blocker.proceed?.()}
							variant='outline'
						>
							{t('admin-applications:intake.leave.leave')}
						</Button>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</FormProvider>
	)
}
